import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  Stack,
  Card,
  Alert,
  Snackbar,
  CardContent,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  TextField,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { getSubscriptionPlans, updateSubscriptionPlan, deleteSubscriptionPlan } from "../../api/apiService";

export default function ManageSubscription () {
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState("");

    const [editOpen, setEditOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [deleteName, setDeleteName] = useState("");

    const [saveLoading, setSaveLoading] = useState(false);

    const totalPages = Math.ceil(rowCount / pageSize) || 0;
    const from = rowCount === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, rowCount);

    const fetchSubscriptionPlans = useCallback(async () => {
      try {
        setLoading(true);
        const res = await getSubscriptionPlans();
        const data = res?.data?.data || [];
        setRows(data.map(plan => ({ ...plan, isActive: plan.isActive !== undefined ? plan.isActive : true })));
        setRowCount(data.length);
      } catch (e) {
        setSnackbar({ open: true, message: "Failed to load subscription plans", severity: "error" });
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => {
      fetchSubscriptionPlans();
    }, [fetchSubscriptionPlans]);

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === "monthlyPrice" || name === "leads" || name === "emails" || name === "campaigns" || name === "teamMembers" || name === "apiCalls") {
            setEditData(prev => ({
                ...prev,
                ...(name === "monthlyPrice" ? { [name]: Number(value) } : {
                    usageLimits: {
                        ...prev.usageLimits,
                        [name]: Number(value)
                    }
                })
            }));
        } else if (name === "aiEmailGeneration" || name === "leadScoring" || name === "advancedAnalytics" || name === "prioritySupport" || name === "customIntegrations" || name === "whiteLabel") {
            setEditData(prev => ({
                ...prev,
                features: {
                    ...prev.features,
                    [name]: checked
                }
            }));
        } else if (name === "popular" || name === "isActive") { // Handle popular and isActive dropdowns
            setEditData(prev => ({
                ...prev,
                [name]: value === "true" // Convert string "true" or "false" to boolean
            }));
        }
        else {
            setEditData(prev => ({
                ...prev,
                [name]: type === "checkbox" ? checked : value
            }));
        }
    };

    const handleUpdateSubscription = async () => {
        if (!editData) return;
        setSaveLoading(true);
        const dataToUpdate = {
            ...editData,
            //popular: editData.popular === "true"? true : false,
            //isActive: editData.isActive === "true"? true : false,
        };
        const { id, ...payload } = dataToUpdate;

        try {
            await updateSubscriptionPlan(editData.id, payload);
            setSnackbar({ open: true, message: 'Subscription plan updated successfully', severity: 'success' });
            setEditOpen(false);
            fetchSubscriptionPlans();
        } catch (e) {
            setSnackbar({ open: true, message: 'Failed to update subscription plan', severity: 'error' });
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDeleteSubscription = async () => {
        if (!deleteId) return;
        try {
            await deleteSubscriptionPlan(deleteId);
            setSnackbar({ open: true, message: 'Subscription plan deleted successfully', severity: 'success' });
            setConfirmOpen(false);
            fetchSubscriptionPlans();
        } catch (e) {
            setSnackbar({ open: true, message: 'Failed to delete subscription plan', severity: 'error' });
        }
    };

    const filteredRows = useMemo(() => {
      const q = search.trim().toLowerCase();
      if (!q) return rows;
      return rows.filter(r => [r.name, r.description, r.currency].some(v => (v || '').toLowerCase().includes(q)));
    }, [rows, search]);

    const columns = [
      { field: "name", headerName: "Plan Name", width: 200,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
            {params.value}
          </Box>
        ),
      },
      { field: "description", headerName: "Description", flex: 1,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
            {params.value}
          </Box>
        ),
      },
      { field: "monthlyPrice", headerName: "Price (USD)", width: 150,
        valueFormatter: (params) => `$${params.value}`,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
            {params.value}
          </Box>
        ),
      },
      { field: "currency", headerName: "Currency", width: 100,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
            {params.value}
          </Box>
        ),
      },
      { field: "popular", headerName: "Popular", width: 120,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
            <Chip
              label={params.value ? "Yes" : "No"}
              color={params.value ? "success" : "default"}
              size="small"
            />
          </Box>
        ),
      },
      { field: "isActive", headerName: "Active", width: 100,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
            <Chip
              label={params.value ? "Yes" : "No"}
              color={params.value ? "success" : "default"}
              size="small"
            />
          </Box>
        ),
      },
      { field: "actions", headerName: "Actions", width: 120,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: 50 }}>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Edit Subscription Plan">
                <IconButton color="primary" onClick={() => { setEditData(params.row); setEditOpen(true); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Subscription Plan">
                <IconButton color="error" onClick={() => { setDeleteId(params.row.id); setDeleteName(params.row.name); setConfirmOpen(true); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        ),
      },
    ];

    return(
        <Box sx={{mb: 2}}>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))} severity={snackbar.severity} sx={{ width: "100%" }}>
              {snackbar.message}
            </Alert>
          </Snackbar>

          <Grid sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5" fontWeight={600}>Manage Subscription Plans</Typography>
            <TextField
              size="small"
              placeholder="Search plans..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 280, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
          </Grid>

          <Box sx={{height:510, width:'100%'}}>
            <Card elevation={0} sx={{borderRadius:2}}>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {rowCount === 0 ? 'No records' : `Showing ${from}–${to} of ${rowCount} records`}
                  </Typography>
                  <Pagination count={totalPages} page={page} onChange={(e, value) => setPage(value)} color="primary" shape="rounded" size="small" />
                </Stack>

                <DataGrid
                  sx={{height:400,borderRadius:2, p:1}}
                  rows={filteredRows}
                  columns={columns}
                  loading={loading}
                  getRowId={(row) => row.id}
                  rowCount={rowCount}
                  paginationMode="server"
                  hideFooterPagination
                  getRowHeight={() => "auto"}
                  disableRowSelectionOnClick
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {rowCount === 0 ? 'No records' : `Showing ${from}–${to} of ${rowCount} records`}
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="page-size-label">Rows per page</InputLabel>
                    <Select labelId="page-size-label" value={pageSize} label="Rows per page" onChange={(e) => { setPageSize(e.target.value); setPage(1); }}>
                      {[10, 20, 50].map((size) => (
                        <MenuItem key={size} value={size}>{size}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination count={totalPages} page={page} onChange={(e, value) => setPage(value)} color="primary" shape="rounded" size="small" />
                </Stack>
              </CardContent>
            </Card>
          </Box>

          <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: 2 }}}}>
            <DialogTitle>Edit Subscription Plan</DialogTitle>
            <DialogContent dividers>
              {editData && (
                <Grid container spacing={2}>
                  <Grid size={{xs:12, sm:6}}>
                    <TextField
                      fullWidth
                      label="Plan Name"
                      name="name"
                      value={editData.name || ''}
                      onChange={handleEditChange}
                      size="small"
                      disabled={true}
                    />
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      value={editData.description || ''}
                      onChange={handleEditChange}
                      size="small"
                      multiline
                      rows={4}
                    />
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <TextField
                      fullWidth
                      label="Monthly Price"
                      name="monthlyPrice"
                      type="number"
                      value={editData.monthlyPrice || 0}
                      onChange={handleEditChange}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <TextField
                      fullWidth
                      label="Currency"
                      name="currency"
                      value={editData.currency || ''}
                      onChange={handleEditChange}
                      size="small"
                    />
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Popular</InputLabel>
                      <Select
                        name="popular"
                        value={String(editData.popular)}
                        label="Popular"
                        onChange={handleEditChange}
                      >
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{xs:12, sm:6}}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Active</InputLabel>
                      <Select
                        name="isActive"
                        value={String(editData.isActive)}
                        label="Active"
                        onChange={handleEditChange}
                      >
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{xs:12}}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Usage Limits</Typography>
                    <Grid container spacing={2}>
                      {editData.usageLimits && Object.entries(editData.usageLimits).map(([key, value]) => (
                        <Grid size={{xs:12, sm:6}} key={key}>
                          <TextField
                            fullWidth
                            label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            name={key}
                            type="number"
                            value={value}
                            onChange={handleEditChange}
                            size="small"
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>

                  <Grid size={{xs:12}}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Features</Typography>
                    <Grid container spacing={2}>
                      {editData.features && Object.entries(editData.features).map(([key, value]) => (
                        <Grid size={{xs:12, sm:6}} key={key}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={value}
                                onChange={handleEditChange}
                                name={key}
                              />
                            }
                            label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>

                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleUpdateSubscription} loading={saveLoading} loadingPosition="end">Save</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="xs" slotProps={{ paper: { sx: { borderRadius: 2 }}}}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete
                <strong> {deleteName || ""} </strong>
                subscription plan?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button color="error" variant="contained" onClick={handleDeleteSubscription}>Delete</Button>
            </DialogActions>
          </Dialog>
        </Box>
    );
}

